import axios from "axios";
import {
  BlogPageData,
  BlogCategory,
  BlogSubcategory,
  BlogPost,
  BlogSearchResult,
} from "@/types/blog";

// Get recent blogs
export const getRecentBlogs = async (): Promise<BlogPost[]> => {
  try {
    const recentBlogsData = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/posts?recent=true`,
    );
    return recentBlogsData?.data?.data?.data || [];
  } catch (error) {
    console.error("Error fetching recent blogs:", error);
    return [];
  }
};

// Check type of blog page (category, subcategory, or post)
export const getBlogPageType = async (
  slug: string,
): Promise<{ status: boolean; data: { type: string } } | null> => {
  try {
    const checkTypeOfBlog_res = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/check-type/${slug}/`,
    );
    const checkTypeOfBlog_data = checkTypeOfBlog_res?.data;

    if (!checkTypeOfBlog_data?.status || !checkTypeOfBlog_data?.data) {
      return null;
    }
    return checkTypeOfBlog_data;
  } catch (error) {
    console.error("Error checking blog page type:", error);
    return null;
  }
};

// Get blog page data based on type
export const getBlogPage = async (
  slug: string,
  checkTypeOfBlog_data: { status: boolean; data: { type: string } } | null,
  ip?: string,
): Promise<BlogPageData | { notFound: boolean }> => {
  if (!checkTypeOfBlog_data?.data?.type) {
    return { notFound: true };
  }

  try {
    let subCatData: BlogPost[] | null = null;
    let catData: BlogCategory | null = null;
    let postData: BlogPost | null = null;

    // Category page
    if (checkTypeOfBlog_data.data.type === "category") {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/category/${slug}`,
      );
      if (res?.data?.status) {
        catData = res.data.data;
      } else {
        catData = null;
      }
    }
    // Sub-category page
    else if (checkTypeOfBlog_data.data.type === "subcategory") {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/posts?subcategory_id=${slug}`,
      );
      if (res.data.status) {
        subCatData = res.data.data.data;
      } else {
        subCatData = null;
      }
    }
    // Blog Post
    else if (checkTypeOfBlog_data.data.type === "post") {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/posts/${slug}/?ip=${ip || ""}`,
      );
      if (res?.data?.status) {
        postData = res.data.data;
      } else {
        postData = null;
        return { notFound: true };
      }
    }

    return {
      type: checkTypeOfBlog_data.data.type as
        | "category"
        | "subcategory"
        | "post",
      data: (postData || catData || subCatData) as
        | BlogCategory
        | BlogSubcategory
        | BlogPost
        | BlogPost[],
    };
  } catch (error) {
    console.error("Error fetching blog page data:", error);
    return { notFound: true };
  }
};

// Fetch all categories for navigation
export const getAllCategories = async (): Promise<BlogCategory[]> => {
  try {
    const response = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/category/`,
    );
    return response.data.data || [];
  } catch (error) {
    console.error("Error fetching categories:", error);
    return [];
  }
};

// Get recent blogs by subcategory
export const handleRecentBlog = async (
  apiSlug: string,
): Promise<BlogPost[]> => {
  try {
    const res = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/posts?recent=true&subcategory_id=${apiSlug}`,
    );
    return res?.data?.data?.data || [];
  } catch (error) {
    console.error("Error fetching recent blogs by subcategory:", error);
    return [];
  }
};

// Search blogs
export const getBlogSearch = async (
  searchTerm: string,
  page: number = 1,
): Promise<BlogSearchResult> => {
  try {
    const res = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/posts?search=${searchTerm}&page_no=${page}`,
    );

    if (res.data.status) {
      return {
        data: res.data.data.data || [],
        count: res.data.data.count || 0,
      };
    } else {
      return {
        data: [],
        count: 0,
      };
    }
  } catch (error) {
    console.error("Error searching blogs:", error);
    return {
      data: [],
      count: 0,
    };
  }
};

// Get preview post
export const getPreviewPost = async (
  detailCatId: string,
): Promise<BlogPost | null> => {
  try {
    const res = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/view-on-site/${detailCatId}`,
    );
    if (res?.data?.status) {
      return res.data.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching preview post:", error);
    return null;
  }
};

// Get IP address from headers
export const getIP = (headerList: Headers): string => {
  try {
    const forwarded = headerList.get("X-Forwarded-For");
    const ip = forwarded
      ? forwarded.split(/, /).find((addr) => !/^(?:unknown|::1)$/.test(addr)) ||
        "127.0.0.1"
      : "127.0.0.1";
    return ip;
  } catch (error) {
    console.error("Error getting IP:", error);
    return "127.0.0.1";
  }
};

// Generate structured data for blog posts
export const generateBlogPostStructuredData = (
  blogData: BlogPost,
  postUrl: string,
) => {
  if (!blogData) return null;

  const structuredData: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blogData.title || "",
    description:
      blogData.meta_description || blogData.summary_description || "",
    image: blogData.f_image_url || "",
    url: postUrl,
    datePublished: blogData.publish_date || "",
    dateModified: blogData.publish_date || "",
    author: {
      "@type": "Person",
      name: blogData.creator || "Kroolo Team",
    },
    publisher: {
      "@type": "Organization",
      name: "Kroolo",
      logo: {
        "@type": "ImageObject",
        url: "https://d2q6bvthx0pflb.cloudfront.net/assets/kroolo_meta_logo.jpeg",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": postUrl,
    },
  };

  // Add keywords if available
  if (blogData.tags_detail && blogData.tags_detail.length > 0) {
    structuredData.keywords = blogData.tags_detail
      .map((tag) => tag.name)
      .join(", ");
  }

  return structuredData;
};

// Legacy function for backward compatibility
export async function fetchData(
  query: { detailid: string },
  req: {
    headers: { host?: string; "x-forwarded-for"?: string };
    connection: { remoteAddress?: string };
  },
) {
  const host = "https://" + (req.headers.host || "kroolo.com");
  const forwarded = req.headers["x-forwarded-for"];
  const ip = forwarded
    ? forwarded.split(/, /)[0]
    : req.connection.remoteAddress;
  const slug = query.detailid;

  try {
    const checkTypeOfBlog_res = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/check-type/${slug}/`,
    );
    const checkTypeOfBlog_data = checkTypeOfBlog_res?.data;

    if (!checkTypeOfBlog_data?.status || !checkTypeOfBlog_data?.data) {
      return {
        notFound: 1,
      };
    }

    let subCatData: BlogPost[] | null = null;
    let catData: BlogCategory | null = null;
    let postData: BlogPost | null = null;

    if (checkTypeOfBlog_data?.data?.type === "category") {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/category/${slug}`,
      );
      if (res?.data?.status) {
        catData = res.data.data;
      } else {
        catData = null;
      }
    } else if (checkTypeOfBlog_data?.data?.type === "subcategory") {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/posts?subcategory_id=${slug}`,
      );
      if (res.data.status) {
        subCatData = res.data.data.data;
      } else {
        subCatData = null;
      }
    } else if (checkTypeOfBlog_data?.data?.type === "post") {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/posts/${slug}/?ip=${ip}`,
      );
      if (res?.data?.status) {
        postData = res.data.data;
      } else {
        return {
          notFound: 1,
        };
      }
    } else {
      return {
        notFound: 1,
      };
    }

    return {
      detailCatId: slug,
      catData: catData || null,
      subCatData: subCatData || null,
      postData: postData || null,
      ip,
      host,
    };
  } catch (error) {
    console.error("Error in fetchData:", error);
    return {
      notFound: 1,
    };
  }
}
